import type { BillStatus, PaymentMethod } from '@six-pockets/shared';
import { toAmount } from '@/utils/format';

/** The payment part of a bill (or of a balance being collected): what is due and how it is being paid. */
export interface PaymentInputs {
  /** The amount owed: the bill's total, or a customer's balance due. */
  total: number;
  status: BillStatus;
  method: PaymentMethod;
  upi: string;
  cash: string;
  /** Partial only: amount being paid now. */
  paid: string;
}

export interface PaymentPreview {
  /** Money taken now (total for settled, paid for partial, 0 otherwise). */
  collect: number;
  balanceDue: number;
  /** collect − (UPI + cash) for split payments. */
  splitLeft: number;
  showPayment: boolean;
  /** Why the payment can't be saved yet, or null when it can. */
  blocker: string | null;
  partialHint: string | null;
}

/**
 * Preview of the payment rules the server applies (server settlement.ts): settled takes the total, partial takes
 * more than 0 and less than the total, and a split must add up to the amount taken.
 */
export function previewPayment(i: PaymentInputs): PaymentPreview {
  const partial = i.status === 'partial';
  const paid = toAmount(i.paid);
  const paidOk = paid > 0 && paid < i.total;
  const showPayment = i.status === 'settled' || partial;

  const collect = i.status === 'settled' ? i.total : partial ? paid : 0;
  const balanceDue = i.status === 'balance' ? i.total : partial ? Math.max(0, i.total - paid) : 0;
  const splitLeft = collect - toAmount(i.upi) - toAmount(i.cash);

  let partialHint: string | null = null;
  if (partial && !paidOk) {
    partialHint = !i.paid
      ? 'Enter how much they are paying now'
      : paid >= i.total
        ? 'Must be less than the total; use Settled for full payment'
        : 'Enter an amount above ₹0';
  }

  let blocker: string | null = null;
  if (partial && !paidOk) blocker = partialHint;
  else if (showPayment && i.method === 'split' && splitLeft !== 0) blocker = 'UPI + Cash must match the amount paid';

  return { collect, balanceDue, splitLeft, showPayment, blocker, partialHint };
}

/** The blocker of a bill with nothing on it. */
export const EMPTY_BILL = 'Select at least one booking or condiment tab';

export interface BillInputs extends Omit<PaymentInputs, 'total'> {
  bookings: { amount: number | null; pausedMs: number; pauseAmount: number | null }[];
  /** Sum of the ticked condiment tabs (with any unsaved additions). */
  condimentsTotal: number;
  /** Number of ticked condiment tabs, including a new one about to be created. */
  saleCount: number;
  discount: string;
}

export interface BillTotals extends PaymentPreview {
  bookingsTotal: number;
  /** Paused time on the bill's bookings and what it costs (already in bookingsTotal); staff may discount it. */
  pausedMs: number;
  pauseAmount: number;
  condimentsTotal: number;
  subtotal: number;
  discount: number;
  total: number;
}

/**
 * Preview of the bill as the server will compute it (see server bill.service / settlement).
 * The server stays the source of truth and re-validates everything.
 */
export function computeBill(i: BillInputs): BillTotals {
  const bookingsTotal = i.bookings.reduce((a, s) => a + (s.amount ?? 0), 0);
  const pausedMs = i.bookings.reduce((a, s) => a + s.pausedMs, 0);
  const pauseAmount = i.bookings.reduce((a, s) => a + (s.pauseAmount ?? 0), 0);
  const condimentsTotal = i.condimentsTotal;
  const subtotal = bookingsTotal + condimentsTotal;
  const discount = Math.min(subtotal, toAmount(i.discount));
  const total = subtotal - discount;

  const payment = previewPayment({ total, status: i.status, method: i.method, upi: i.upi, cash: i.cash, paid: i.paid });
  const blocker = i.bookings.length === 0 && i.saleCount === 0 ? EMPTY_BILL : payment.blocker;

  return { bookingsTotal, pausedMs, pauseAmount, condimentsTotal, subtotal, discount, total, ...payment, blocker };
}
